// Global Instructions Rule Applied!
//
// Copy a value to the clipboard.
//
// There was no shared copy control before this — five surfaces had each
// hand-rolled navigator.clipboard.writeText with their own feedback (email
// merge fields and templates, wire instructions, send-communication). This is
// the house version; the error copy is lifted from MergeFieldsButton so the
// failure message stays the one users already see. Migrating those five call
// sites is deliberately left out of the change that introduced this.

import React, { useEffect, useRef, useState } from 'react'
import { Button as AntButton, Tooltip } from 'antd'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCopy } from '@fortawesome/free-solid-svg-icons'

export interface CopyButtonProps {
  /** The exact string placed on the clipboard. */
  value: string
  label?: string
  /** Toast shown on success. Omit for silent copy (the tick still shows). */
  successMessage?: string
  size?: 'small' | 'middle'
  disabled?: boolean
  /** Compact inline affordance (type='text', icon only, no label text) —
   *  used beside emails / phone numbers / URLs in read-mode fields. */
  iconOnly?: boolean
}

export function CopyButton({
  value,
  label = 'Copy',
  successMessage,
  size = 'small',
  disabled = false,
  iconOnly = false
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // The tick is on a timer, so it has to be cleared if the button unmounts
  // first (closing the drawer it sits in, for instance).
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    []
  )

  const handleCopy = async (e: React.MouseEvent) => {
    // Never let the click reach the surface underneath (a read-mode field
    // would enter edit mode).
    e.stopPropagation()
    e.preventDefault()
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      if (successMessage) toast.success(successMessage)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Clipboard copy failed — select and copy manually.')
    }
  }

  return (
    <Tooltip title={copied ? 'Copied' : `Copy ${label === 'Copy' ? 'to clipboard' : label}`}>
      <AntButton
        size={size}
        type={iconOnly ? 'text' : 'default'}
        disabled={disabled || !value}
        onClick={handleCopy}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label={label}
        icon={<FontAwesomeIcon icon={copied ? faCheck : faCopy} />}
      >
        {iconOnly ? null : copied ? 'Copied' : label}
      </AntButton>
    </Tooltip>
  )
}
